import React, { useRef, useState } from "react";
import componentSizes from "@streeteasy/design-system/constants/componentSizes";
import { buttonThemes } from "@streeteasy/design-system/deprecated/actions/button";
import { PrimaryMedium } from "@streeteasy/design-system/typography";
import SwiperCore, { A11y, Keyboard, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react";

import { smallScreen } from "src/shared/constants/screenSizes";

import useSwiperRef from "../../hooks/useSwiperRef";
import { recommendedListingsPropType } from "../../propTypes";
import RecommendedListing from "../RecommendedListing";
import * as S from "./styled";

SwiperCore.use([Keyboard, Navigation]);

export default function RecommendedListings({ recommendedListings }) {
  const [isAllVisible, setIsAllVisible] = useState(false);

  const swiperRef = useRef();
  const [prevEl, prevElRef] = useSwiperRef();
  const [nextEl, nextElRef] = useSwiperRef();

  return (
    <S.Container id="recommendations">
      <PrimaryMedium>Recommended listings</PrimaryMedium>
      <S.SectionSubheading as="h3">
        Based on your search activity
      </S.SectionSubheading>
      <S.DesktopListingsContainer>
        <S.PreviousButton ref={prevElRef} disabled type="previous">
          Previous recommended listing
        </S.PreviousButton>
        <S.NextButton ref={nextElRef} disabled type="next">
          Next recommended listing
        </S.NextButton>
        <Swiper
          a11y={{
            prevSlideMessage: "Previous listing",
            nextSlideMessage: "Next listing"
          }}
          breakpoints={{
            [smallScreen]: {
              slidesPerView: 2
            },
            1060: {
              slidesPerView: 3
            },
            1430: {
              slidesPerView: 4
            }
          }}
          keyboard={{ enabled: true, onlyInViewport: true }}
          modules={[Navigation, A11y]}
          navigation={{
            prevEl,
            nextEl
          }}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          spaceBetween={26}
        >
          {recommendedListings.map(
            ({ listing, tracking_id, was_helpful }, index) => (
              <SwiperSlide
                key={listing.id}
                onFocus={event => {
                  /*
                   * This is necessary to fix an accessibility bug in the slider.
                   * More info at https://github.com/nolimits4web/swiper/issues/4006
                   */

                  if (
                    !swiperRef.current ||
                    event.target.nodeName === "BUTTON"
                  ) {
                    return;
                  }

                  swiperRef.current.el.scrollLeft = 0;
                  // eslint-disable-next-line no-unused-expressions
                  swiperRef.current?.slideTo(index);
                }}
              >
                <RecommendedListing
                  listing={listing}
                  trackingId={tracking_id}
                  wasHelpful={was_helpful}
                />
              </SwiperSlide>
            )
          )}
        </Swiper>
      </S.DesktopListingsContainer>
      <S.MobileListingsContainer>
        {recommendedListings.map(
          ({ listing, model, tracking_id, was_helpful }, index) => {
            if (index > 2 && !isAllVisible) return null;

            return (
              <SwiperSlide key={listing.id}>
                <S.MobileListingCardContainer>
                  <RecommendedListing
                    listing={listing}
                    listingType={model}
                    trackingId={tracking_id}
                    wasHelpful={was_helpful}
                  />
                </S.MobileListingCardContainer>
              </SwiperSlide>
            );
          }
        )}

        {recommendedListings.length > 3 && !isAllVisible && (
          <S.ViewMoreButton
            onClick={() => setIsAllVisible(true)}
            size={componentSizes.LG}
            theme={buttonThemes.SECONDARY}
          >
            See all
          </S.ViewMoreButton>
        )}
      </S.MobileListingsContainer>
    </S.Container>
  );
}

RecommendedListings.propTypes = {
  recommendedListings: recommendedListingsPropType.isRequired
};
